import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Accordion, AccordionSummary, AccordionDetails,
  Chip, IconButton, Typography, Box, TextField, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, Tooltip, Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, Edit as EditIcon, Delete as DeleteIcon,
  AddCircle as AddCircleIcon, Search as SearchIcon, Star as StarIcon,
  Public as PublicIcon, Brightness7 as CustomIcon,
  People as PeopleIcon, Business as BusinessIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const formatRA = (degrees) => {
  if (degrees === null || degrees === undefined) return '-';
  const hours = degrees / 15;
  const h = Math.floor(hours);
  const m = Math.floor((hours - h) * 60);
  const s = ((hours - h) * 60 - m) * 60;
  return `${h.toString().padStart(2, '0')}h ${m.toString().padStart(2, '0')}m ${s.toFixed(1)}s`;
};

const formatDec = (degrees) => {
  if (degrees === null || degrees === undefined) return '-';
  const sign = degrees >= 0 ? '+' : '-';
  const abs = Math.abs(degrees);
  const d = Math.floor(abs);
  const m = Math.floor((abs - d) * 60);
  const s = ((abs - d) * 60 - m) * 60;
  return `${sign}${d}\u00B0 ${m.toString().padStart(2, '0')}' ${s.toFixed(1)}"`;
};

const TARGET_TYPE_ICONS = {
  exoplanet: <PublicIcon fontSize="small" />,
  variable_star: <StarIcon fontSize="small" />,
  deep_sky: <StarIcon fontSize="small" />,
  asteroid: <PublicIcon fontSize="small" />,
  custom: <CustomIcon fontSize="small" />,
};

const TARGET_TYPE_COLORS = {
  exoplanet: 'primary',
  variable_star: 'warning',
  deep_sky: 'info',
  asteroid: 'secondary',
  custom: 'default',
};

const getOwnerId = (target) => {
  if (typeof target.user_id === 'object' && target.user_id?._id) return target.user_id._id;
  return target.user_id;
};

const getOwnerName = (target) => {
  if (typeof target.user_id === 'object' && target.user_id?.username) return target.user_id.username;
  return null;
};

const TargetTable = ({
  targets,
  projects,
  loading,
  currentUserId,
  onEdit,
  onDelete,
  onNewTemplate,
  onRefresh,
  searchTerm,
  onSearchChange,
  filterType,
  onFilterTypeChange,
  filterProject,
  onFilterProjectChange,
}) => {
  // Filter targets based on search term and filters
  const filteredTargets = React.useMemo(() => {
    if (!targets) return [];
    return targets.filter(target => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const nameMatch = target.name?.toLowerCase().includes(term);
        const tagMatch = target.tags?.some(tag => tag.toLowerCase().includes(term));
        if (!nameMatch && !tagMatch) return false;
      }
      // Type filter
      if (filterType && filterType !== 'all') {
        if (target.target_type !== filterType) return false;
      }
      // Project filter
      if (filterProject && filterProject !== 'all') {
        const tProjectId = target.project_id?._id || target.project_id;
        if (tProjectId !== filterProject) return false;
      }
      return true;
    });
  }, [targets, searchTerm, filterType, filterProject]);

  // Group filtered targets by project_id
  const groupedTargets = React.useMemo(() => {
    const grouped = {};
    filteredTargets.forEach(target => {
      const projectId = target.project_id?._id || target.project_id || 'no-project';
      if (!grouped[projectId]) {
        grouped[projectId] = [];
      }
      grouped[projectId].push(target);
    });
    return grouped;
  }, [filteredTargets]);

  // Build project lookup map
  const projectMap = React.useMemo(() => {
    const map = {};
    if (projects) {
      projects.forEach(p => {
        map[p._id] = p;
      });
    }
    return map;
  }, [projects]);

  // Get unique target types for filter dropdown
  const targetTypes = React.useMemo(() => {
    if (!targets) return [];
    const types = new Set(targets.map(t => t.target_type).filter(Boolean));
    return Array.from(types).sort();
  }, [targets]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography color="text.secondary">Loading targets...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search and filter controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by name or tag..."
          value={searchTerm || ''}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Target Type</InputLabel>
          <Select
            value={filterType || 'all'}
            label="Target Type"
            onChange={(e) => onFilterTypeChange(e.target.value)}
          >
            <MenuItem value="all">All Types</MenuItem>
            {targetTypes.map(type => (
              <MenuItem key={type} value={type}>
                {type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Project</InputLabel>
          <Select
            value={filterProject || 'all'}
            label="Project"
            onChange={(e) => onFilterProjectChange(e.target.value)}
          >
            <MenuItem value="all">All Projects</MenuItem>
            {projects && projects.map(p => (
              <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Chip
          label={`${filteredTargets.length} target${filteredTargets.length !== 1 ? 's' : ''}`}
          color="primary"
          size="small"
        />
      </Box>

      {/* Empty state */}
      {Object.keys(groupedTargets).length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary" align="center">
            {searchTerm || (filterType && filterType !== 'all') || (filterProject && filterProject !== 'all')
              ? 'No targets match the current filters.'
              : 'No targets in the library. Add your first target to get started.'}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {Object.entries(groupedTargets).map(([projectId, projectTargets]) => {
            const project = projectMap[projectId];
            return (
              <Accordion key={projectId} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                    <Typography sx={{ flexGrow: 1, fontWeight: 500 }}>
                      {project ? project.name : 'No Project Assigned'}
                    </Typography>
                    <Chip
                      label={`${projectTargets.length} target${projectTargets.length !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>RA</TableCell>
                          <TableCell>Dec</TableCell>
                          <TableCell>Tags</TableCell>
                          <TableCell>Last Used</TableCell>
                          <TableCell>Observations</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {projectTargets.map(target => {
                          const isOwner = !currentUserId || getOwnerId(target) === currentUserId;
                          const ownerName = getOwnerName(target);
                          return (
                          <TableRow key={target._id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" fontWeight={500}>
                                  {target.name}
                                </Typography>
                                {target.visibility === 'observatory' && (
                                  <Tooltip title="Shared with observatory">
                                    <PeopleIcon fontSize="small" color="info" sx={{ fontSize: 16 }} />
                                  </Tooltip>
                                )}
                                {target.visibility === 'organization' && (
                                  <Tooltip title="Shared with organization">
                                    <BusinessIcon fontSize="small" color="secondary" sx={{ fontSize: 16 }} />
                                  </Tooltip>
                                )}
                              </Box>
                              {!isOwner && ownerName && (
                                <Typography variant="caption" color="text.secondary">
                                  by {ownerName}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={TARGET_TYPE_ICONS[target.target_type] || TARGET_TYPE_ICONS.custom}
                                label={target.target_type
                                  ? target.target_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                                  : 'Unknown'}
                                size="small"
                                color={TARGET_TYPE_COLORS[target.target_type] || 'default'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {formatRA(target.ra)}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {formatDec(target.dec)}
                            </TableCell>
                            <TableCell>
                              {target.tags && target.tags.length > 0 ? (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {target.tags.map((tag, idx) => (
                                    <Chip
                                      key={idx}
                                      label={tag}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {target.last_used_at
                                  ? formatDistanceToNow(new Date(target.last_used_at), { addSuffix: true })
                                  : 'Never'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {target.observation_count != null ? target.observation_count : 0}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={isOwner ? "Edit target" : "Only the owner can edit"}>
                                <span>
                                  <IconButton size="small" onClick={() => onEdit(target)} disabled={!isOwner}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Create observation from target">
                                <IconButton size="small" color="primary" onClick={() => onNewTemplate(target)}>
                                  <AddCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={isOwner ? "Delete target" : "Only the owner can delete"}>
                                <span>
                                  <IconButton size="small" color="error" onClick={() => onDelete(target)} disabled={!isOwner}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default TargetTable;
